// validation.ts

import { toMinutes } from './helper';

export const BaseValidation: Record<string, RegExp> = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
  password: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
  atleastOneAlphaOneNumeric: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/,
  alphabatic: /^[A-Za-z]+$/,
  nameAlphabatic: /^[A-Za-z ]+$/,
  numeric: /^[0-9]+$/,
  price: /^\d+(\.\d{1,2})?$/,
  username: /^[A-Za-z0-9 ]{2,20}$/,
  gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  serialNumber: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9-]{6,30}$/,
  phone: /^[6-9]\d{9}$/,
  vehicleNumber: /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/,
  pinCode: /^[1-9][0-9]{5}$/,
  hexColor: /^#?[A-Fa-f0-9]{6}$/,
  insuranceNumber: /^[A-Za-z0-9][A-Za-z0-9-/]{4,18}[A-Za-z0-9]$/,
  pfNumber: /^[A-Z]{2}\/[A-Z]{3}\/\d{1,5}\/\d{1,7}$/,
  holidayName: /^(?=.*[A-Za-z])[A-Za-z &]+$/,
} as const;

// A generic type for a validation rule that has a value and a message
type Rule<T> = {
  value: T;
  message: string;
};

// Defines the structure for a set of validation rules for a single field
type FieldValidationRules = {
  pattern?: Rule<RegExp>;
  maxLength?: Rule<number>;
  minLength?: Rule<number>;
  validate?: (value: string) => boolean | string;
};

// Defines the overall shape of the errorTypes object
type ErrorTypes = {
  required: (fieldName?: string) => { required: string };
  checkInTime: (checkOutTime: string) => FieldValidationRules;
  checkOutTime: (checkInTime: string) => FieldValidationRules;
  checkInDate: (checkOutDate: string) => FieldValidationRules;
  checkOutDate: (checkInDate: string) => FieldValidationRules;
  noSunday: FieldValidationRules;
  email: FieldValidationRules;
  phone: FieldValidationRules;
  password: FieldValidationRules;
  name: FieldValidationRules;
  gstNumber: FieldValidationRules;
  pinCode: FieldValidationRules;
  aadhaar: FieldValidationRules;
  pan: FieldValidationRules;
  uanNumber: FieldValidationRules;
  colorCode: FieldValidationRules;
  esic: FieldValidationRules;
  drivingLicense: FieldValidationRules;
  accountNumber: FieldValidationRules;
  ifscCode: FieldValidationRules;
  capitalName: FieldValidationRules;
  level: FieldValidationRules;
  insuranceNumber: FieldValidationRules;
  serialNumber: FieldValidationRules;
  pfNumber: FieldValidationRules;
  holidayName: FieldValidationRules;

  // Add other validation types here as needed
};

export const errorTypes: ErrorTypes = {
  required: (fieldName = 'This field') => ({
    required: `${fieldName} is required`,
  }),
  checkInTime: (checkOutTime: string) => ({
    validate: (value: string) => {
      const inMin = toMinutes(value);
      const outMin = toMinutes(checkOutTime);
      if (outMin && inMin >= outMin) {
        return 'Check-in time must be before check-out time';
      }
      return true;
    },
  }),
  checkOutTime: (checkInTime: string) => ({
    validate: (value: string) => {
      const outMin = toMinutes(value);
      const inMin = toMinutes(checkInTime);
      if (inMin && inMin >= outMin) {
        return 'Check-out time must be after check-in time';
      }
      return true;
    },
  }),
  checkInDate: (checkOutDate: string) => ({
    validate: (value: string) => {
      const date = new Date(value);
      if (date.getDay() === 0) {
        return 'Sunday is a non-working day. Please select another date.';
      }
      const inDate = date.setHours(0, 0, 0, 0);
      const outDate = new Date(checkOutDate).setHours(0, 0, 0, 0);
      if (outDate && inDate > outDate) {
        return 'Check-in date must be before check-out date';
      }
      return true;
    },
  }),
  checkOutDate: (checkInDate: string) => ({
    validate: (value: string) => {
      const date = new Date(value);
      if (date.getDay() === 0) {
        return 'Sunday is a non-working day. Please select another date.';
      }
      const inDate = date.setHours(0, 0, 0, 0);
      const outDate = new Date(checkInDate).setHours(0, 0, 0, 0);
      if (outDate && outDate > inDate) {
        return 'Check-out date must be after check-in date';
      }
      return true;
    },
  }),
  noSunday: {
    validate: (value: string) => {
      if (new Date(value).getDay() === 0) {
        return 'Sunday is a non-working day. Please select another date.';
      }
      return true;
    },
  },
  email: {
    pattern: {
      value: BaseValidation.email,
      message: 'Invalid email address',
    },
    maxLength: {
      value: 254,
      message: 'Email cannot exceed 254 characters',
    },
    validate: (value) => {
      if (value !== value?.trim())
        return 'Email cannot have leading or trailing spaces';
      if (value !== value.toLowerCase()) return 'Email must be in lowercase';
      // if (!value.endsWith('@outlook.com')) return 'Email must be a company email';
      return true;
    },
  },
  insuranceNumber: {
    pattern: {
      value: BaseValidation.insuranceNumber,
      message: 'Please enter a valid insurance number',
    },
  },
  serialNumber: {
    pattern: {
      value: BaseValidation.serialNumber,
      message: 'Please enter a valid serial number',
    },
  },
  holidayName: {
    pattern: {
      value: BaseValidation.holidayName,
      message: ' Holiday name must contain only letters and spaces',
    },
  },
  phone: {
    pattern: {
      value: BaseValidation.phone,
      message: 'Please enter a valid 10-digit mobile number',
    },
    maxLength: {
      value: 10,
      message: 'Mobile number cannot be more than 10 digits',
    },
    minLength: {
      value: 10,
      message: 'Mobile number must be exactly 10 digits',
    },
  },
  password: {
    minLength: {
      value: 6,
      message: 'Password must be at least 6 characters long',
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/,
      message:
        'Password must include uppercase, lowercase, number, and special character',
    },
  },
  name: {
    pattern: {
      value: /^[a-zA-Z\s]*$/,
      message: 'Name must contain only letters and spaces',
    },
    maxLength: {
      value: 160,
      message: 'Name should not exceed 160 characters',
    },
  },
  capitalName: {
    pattern: {
      value: /^[A-Z][a-zA-Z\s]*$/,
      message:
        'Name must start with a capital letter and contain only letters and spaces',
    },
    minLength: {
      value: 2,
      message: 'Name must be at least 2 characters long',
    },
    maxLength: {
      value: 20,
      message: 'Name should not exceed 20 characters',
    },
  },
  pfNumber: {
    pattern: {
      value: BaseValidation.pfNumber,
      message: 'Please enter valid pf number',
    },
  },
  gstNumber: {
    pattern: {
      value: BaseValidation?.gst,
      message:
        'Please enter a valid 15-digit GST number (e.g., 22ABCDE1234F1Z5)',
    },
    maxLength: {
      value: 15,
      message: 'GST number must be 15 characters',
    },
    minLength: {
      value: 15,
      message: 'GST number must be 15 characters',
    },
  },

  pinCode: {
    pattern: {
      value: BaseValidation.pinCode,
      message: 'Please enter a valid 6-digit PIN code',
    },
    maxLength: {
      value: 6,
      message: 'PIN code must be exactly 6 digits',
    },
    minLength: {
      value: 6,
      message: 'PIN code must be exactly 6 digits',
    },
  },

  aadhaar: {
    pattern: {
      value: /^[2-9]{1}[0-9]{11}$/,
      message:
        'Invalid Aadhaar Number (must be 12 digits, not starting with 0/1)',
    },
  },
  pan: {
    pattern: {
      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      message: 'Invalid PAN format (e.g., ABCDE1234F)',
    },
  },
  uanNumber: {
    pattern: {
      value: /^[0-9]{12}$/,
      message: 'UAN must be exactly 12 digits',
    },
  },
  level: {
    pattern: {
      value: /^[0-9]+$/,
      message: 'Level must be a number',
    },
    validate: (value: string) => {
      const num = Number(value);
      if (!Number.isInteger(num)) return 'Level must be an integer';
      if (num < 1) return 'Level cannot be less than 1';
      if (num > 10) return 'Level cannot be greater than 10';
      return true;
    },
  },
  esic: {
    pattern: {
      value: /^[0-9]{10,17}$/,
      message: 'ESIC number must be between 10 to 17 digits',
    },
  },
  drivingLicense: {
    pattern: {
      value: /^[A-Z]{2}[0-9]{2}[0-9A-Z]{11,13}$/,
      message: 'Invalid Driving License format (e.g., RJ1412345678912)',
    },
  },
  accountNumber: {
    pattern: {
      value: /^[0-9]{9,18}$/,
      message: 'Invalid Account Number (must be between 9 to 18 digits)',
    },
  },
  ifscCode: {
    pattern: {
      value: /^[A-Z0-9]*$/,
      message: 'It must contain only numbers and letters',
    },
  },
  colorCode: {
    pattern: {
      value: BaseValidation.hexColor,
      message: 'Please enter a valid 6-digit hex color (e.g. #A1B2C3)',
    },
    validate: (value) =>
      value.startsWith('#') || 'Color code must start with #',
  },
};
